import { useEffect, useState } from "react";
import { imageManager } from "@services/image-manager";

/**
 * In-memory cache for storing image Blob URLs.
 * The key is the image source (src), and the value is the object URL.
 */
const memoryCache = new Map<string, string>();

/**
 * Maximum number of items allowed in the cache.
 * When the limit is reached, the oldest entry is removed.
 */
const MAX_CACHE_SIZE = 100;

interface UseLazyImageReturn {
  /** The final image URL, or null if the image could not be loaded */
  imageUrl: string | null;

  /** Indicates whether an error occurred while fetching or processing the image */
  hasError: boolean;
}

/**
 * Custom React hook to lazily load and manage an image as a Blob URL.
 *
 * This hook:
 * - Checks if the image is already cached in memory.
 * - If not cached, fetches the image Blob using `imageManager`, creates an object URL, and stores it in the cache.
 * - Cleans up the object URL when the component unmounts (if not cached).
 *
 * Why both in-memory cache and browser storage?
 * - In-memory cache: ultra-fast access during the current SPA session, avoiding repeated API calls.
 * - Browser storage: persistence across page refreshes or new sessions, reducing network usage.
 *
 * Memory lifecycle:
 * - Cache is cleared on page refresh or tab close.
 * - Limited to 100 images to prevent excessive memory usage.
 */
function addToCache(src: string, url: string): void {
  if (memoryCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = memoryCache.keys().next().value;
    if (oldestKey) {
      const oldestUrl = memoryCache.get(oldestKey);
      if (oldestUrl) {
        URL.revokeObjectURL(oldestUrl);
      }
      memoryCache.delete(oldestKey);
    }
  }
  memoryCache.set(src, url);
}

/**
 * Custom React hook to lazily load and manage an image as a Blob URL.
 *
 * This hook:
 * - Checks if the image is already cached in memory.
 * - If not cached, fetches the image Blob using `imageManager`, creates an object URL, and stores it in the cache.
 * - Cleans up the object URL when the component unmounts (if not cached).
 */
export function useLazyImage(
  src: string,
  companyId: string,
  contextType: string,
  contextId: string
): UseLazyImageReturn {
  const [imageUrl, setImageUrl] = useState<string | null>(
    memoryCache.get(src) ?? null
  );
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = memoryCache.get(src) ?? null;

    async function loadImage(): Promise<void> {
      try {
        if (!objectUrl) {
          const blob = await imageManager.getImageBlob(
            src,
            companyId,
            contextType,
            contextId
          );
          objectUrl = URL.createObjectURL(blob);
          addToCache(src, objectUrl);
        }
        setImageUrl(objectUrl);
      } catch {
        setHasError(true);
        setImageUrl(null);
      }
    }

    void loadImage();

    return () => {
      // Revoke only if the URL is not cached anymore
      if (objectUrl && !memoryCache.has(src)) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, companyId, contextType, contextId]);

  return { imageUrl, hasError };
}
